import { PropsWithChildren } from 'react';

import { PropsWithCN, cn } from '@tg-web/utils';

export type IslandProps = PropsWithChildren & PropsWithCN;

/**
 * The corner radius of this system, and of every button in it.
 *
 * A literal rather than a token because there is no radius token in this
 * codebase and this is not the change that invents one -- but it is the *same*
 * literal everywhere, so a new component inherits the answer instead of picking
 * one. Circular controls are the exception, and only because a circle is not a
 * rounded rectangle: see `IslandCircleButton`.
 */
export const ISLAND_RADIUS_CLASSNAME = 'rounded-[24px]';

/**
 * For the few places that need an island's chrome on a component that already
 * owns its own element -- a Radix group root, say -- where wrapping would add a
 * div between the island and its cells.
 */
// `event-surface` marks it as a surface an event theme may tint; it does
// nothing anywhere else (see app/styles.css).
export const islandClassName = `event-surface bg-tg-section-bg blur-shadow-small flex w-full shrink-0 flex-col overflow-hidden ${ISLAND_RADIUS_CLASSNAME}`;

/**
 * The grouped-list container of the island design system.
 *
 * `overflow-hidden` is load-bearing rather than cosmetic: cells paint their
 * pressed state as a full-bleed background, and without the clip that highlight
 * squares off the island's first and last corners. It also means cells never
 * need to round themselves positionally, which is the trap `ViewField` carries.
 *
 * There is no vertical padding, so the first and last cells sit flush against
 * the rounded edge and the clip is what actually shapes their highlight.
 *
 * The radius comes from `ISLAND_RADIUS_CLASSNAME` above, which every button in
 * the system shares.
 *
 * `shrink-0` is load-bearing for a reason that is not about the island itself.
 * Pages put these straight into `PageWrapper`, which is a definite-height flex
 * column (body is pinned to 100vh by AdditionalScrollFixer) and also the scroll
 * container. A flex item whose overflow is not `visible` has no automatic
 * minimum size, so the `overflow-hidden` above let an island shrink to nothing:
 * the rows squashed and clipped, the content never exceeded the column, and
 * `overflow-auto` therefore never engaged. Without this the page stops
 * scrolling on a short window instead of scrolling.
 */
export function Island({ children, className }: IslandProps) {
  return <div className={cn(islandClassName, className)}>{children}</div>;
}
